"use client"

import * as React from "react"
import { capitalizeFirst, cn, formatLocation } from "../../lib/utils"

import ClockIcon from "../icons/ClockIcon"
import FrameIcon from "../icons/FrameIcon"
import OfficeBagIcon from "../icons/OfficeBagIcon"
import LocationSmIcon from "../icons/locationSmIcon"

export interface JobCardPublicProps {
  jobTitle: string
  companyName: string
  location?: string
  city?: string | null
  state?: string | null
  country?: string | null
  salary: string
  experience: string
  workMode: string
  skills?: string[]
  companyLogo?: string
  className?: string
  icon?: React.ReactNode
  summaryText?: string
  hideSalary?: boolean
  cpaValue?: number | null
  cpcValue?: number | null
}

const toTitleCase = (value: string) =>
  value
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase())
    .trim();

export function JobCardPublic({
  jobTitle,
  companyName,
  location,
  city,
  state,
  country,
  salary,
  experience,
  workMode,
  companyLogo,
  icon,
  skills = [],
  summaryText,
  className,
  hideSalary,
  cpaValue,
  cpcValue,
}: JobCardPublicProps) {
  const MAX_SKILL_ROWS = 2
  const badgeClassName = cn(
    "rounded-[4px] border border-[#0D41E1] bg-[#E6F9FD] px-2 py-0.5 text-[12px] font-medium text-[#0D41E1]"
  )
  const displayLocation = formatLocation({ city, state, country, location })
  const skillsContainerRef = React.useRef<HTMLDivElement | null>(null)
  const measureContainerRef = React.useRef<HTMLDivElement | null>(null)
  const [containerWidth, setContainerWidth] = React.useState<number | null>(null)
  const [visibleSkillCount, setVisibleSkillCount] = React.useState(skills.length)
  const metaBadges: { label: string; icon: React.ReactNode }[] = []

  if (experience) {
    metaBadges.push({ label: experience, icon: <OfficeBagIcon color="#98A2B3" /> })
  }
  const workModeLabel = capitalizeFirst(workMode)
  if (workModeLabel) {
    metaBadges.push({ label: workModeLabel, icon: <ClockIcon color="#98A2B3" /> })
  }
  if (!hideSalary && salary) {
    metaBadges.push({ label: salary, icon: <FrameIcon color="#98A2B3" /> })
  }

  const epaValue = cpaValue ?? cpcValue ?? null;
  const epaLabel = cpaValue != null ? "RPA" : cpcValue != null ? "RPC" : null;

  React.useLayoutEffect(() => {
    if (!skillsContainerRef.current) return
    const element = skillsContainerRef.current
    const updateWidth = () => setContainerWidth(element.clientWidth)
    updateWidth()
    const observer = new ResizeObserver(updateWidth)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  React.useEffect(() => {
    setVisibleSkillCount(skills.length)
  }, [skills.length])

  React.useLayoutEffect(() => {
    if (!measureContainerRef.current || skills.length === 0 || containerWidth === null)
      return
    const children = Array.from(
      measureContainerRef.current.children
    ) as HTMLElement[]
    if (children.length === 0) return

    const rowTops: number[] = []
    let secondRowTop: number | null = null
    let firstOverflowIndex: number | null = null

    children.forEach((child, index) => {
      const top = child.offsetTop
      if (!rowTops.includes(top)) {
        rowTops.push(top)
        if (rowTops.length === MAX_SKILL_ROWS) {
          secondRowTop = top
        }
      }
      if (secondRowTop !== null && top > secondRowTop && firstOverflowIndex === null) {
        firstOverflowIndex = index
      }
    })

    if (rowTops.length <= MAX_SKILL_ROWS || firstOverflowIndex === null) return

    const nextCount =
      firstOverflowIndex === visibleSkillCount
        ? Math.max(0, visibleSkillCount - 1)
        : Math.max(0, Math.min(firstOverflowIndex, visibleSkillCount - 1))
    if (nextCount !== visibleSkillCount) {
      setVisibleSkillCount(nextCount)
    }
  }, [containerWidth, skills, visibleSkillCount])

  const clampedVisibleSkillCount =
    skills.length > 0 ? Math.max(1, Math.min(visibleSkillCount, skills.length)) : 0
  const overflowCount = Math.max(0, skills.length - clampedVisibleSkillCount)

  return (
    <article
      className={cn(
        "creator-job-card w-full rounded-[8px] border border-black bg-white p-[12px] flex flex-col gap-[12px]",
        className
      )}
    >
      <div className="flex items-start gap-2">
        {icon ?? (
          <div className="h-[42px] w-[42px] shrink-0 rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center text-sm font-semibold text-[#475467]">
            {companyLogo ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={companyLogo}
                alt={companyName || "Company logo"}
                className="block h-full w-full object-contain"
              />
            ) : (
              companyName?.charAt(0) ?? "dY?›"
            )}
          </div>
        )}
        <div className="flex flex-1 flex-col gap-[2px] min-w-0">
          <p
            className="text-[16px] font-semibold text-[#101828] leading-[normal] line-clamp-2"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {jobTitle}
          </p>
          <div className="flex flex-col gap-1 min-w-0">
            <p className="text-[14px] text-[#475467]">{companyName}</p>
            {displayLocation ? (
              <div className="flex items-center gap-1 text-[14px] text-[#475467] min-w-0">
                <LocationSmIcon className="h-3.5 w-3.5 text-[#98A2B3]" />
                <span className="truncate" title={displayLocation}>
                  {displayLocation}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {metaBadges.length > 0 && (
        <div className="flex flex-wrap gap-[12px]">
          {metaBadges.map((badge) => (
            <div
              key={`${badge.label}-${badge.icon ? "icon" : "text"}`}
              className="flex items-center gap-[6px]"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center text-[#98A2B3]">
                {badge.icon}
              </span>
              <span className="text-[14px] font-medium leading-6 text-[#475467]">
                {badge.label}
              </span>
            </div>
          ))}
        </div>
      )}

      {skills.length > 0 && (
        <div className="relative">
          <div ref={skillsContainerRef} className="flex flex-wrap gap-[6px]">
            {skills.slice(0, clampedVisibleSkillCount).map((skill) => (
              <span key={skill} className={badgeClassName}>
                {toTitleCase(skill)}
              </span>
            ))}
            {overflowCount > 0 && <span className={badgeClassName}>+{overflowCount}</span>}
          </div>
          <div
            ref={measureContainerRef}
            className="pointer-events-none absolute left-0 top-0 -z-10 flex flex-wrap gap-[6px] opacity-0"
            style={{ width: containerWidth ?? undefined }}
          >
            {skills.slice(0, visibleSkillCount).map((skill) => (
              <span key={`${skill}-measure`} className={badgeClassName}>
                {toTitleCase(skill)}
              </span>
            ))}
            {visibleSkillCount < skills.length && (
              <span className={badgeClassName}>+{skills.length - visibleSkillCount}</span>
            )}
          </div>
        </div>
      )}

      {summaryText ? (
        <div className="rounded-[8px] bg-[#E6F9FD] p-[10px]">
          <div className="flex items-start gap-[6px]">
            <span className="flex h-3.5 w-3.5 items-center justify-center text-[#0D41E1]">
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M8.54431 1.35449C6.00213 2.80213 4.87368 4.17213 4.6955 7.63862H12.2581C12.3848 3.16875 8.54431 1.35449 8.54431 1.35449Z"
                  stroke="#101828"
                  fill="#0D41E1"
                />
              </svg>
            </span>
            <span className="text-[14px] font-medium text-[#0D41E1]">
              {summaryText}
            </span>
          </div>
        </div>
      ) : null}

      {epaValue != null && (
        <div className="flex items-center justify-between border-t border-gray-100 pt-2.5">
          <div className="flex items-end gap-1.5 text-[#344054]">
            <span className="text-sm font-medium">{epaLabel}:</span>
            <span className="text-[22px] font-bold leading-none">${Number(epaValue).toFixed(2)}</span>
          </div>
        </div>
      )}
    </article>
  )
}
